<script setup lang="ts">
    import { FormInputData } from '~~/composables/useFormInput';
    import type { LocaleByCountry } from '~~/composables/useAvailableLocalesByCountry';

    const allLocales = useAvailableLocalesByCountry();
    const props = withDefaults(
        defineProps<{
            inputData: FormInputData;
            labelRightClass?: string;
        }>(),
        {
            labelRightClass: 'pb-1',
        }
    );

    /**
     * The locales the pill offers. Bounded when the consumer scopes them
     * (`localeOptions`) — Ma page only offers the locales of the profile's
     * country (TDD-3784). Unbounded otherwise, which is every other screen.
     */
    const availableLocales = computed<Record<string, LocaleByCountry[]>>(() => {
        const allowed = props.inputData.localeOptions;
        if (!allowed?.length) return allLocales.value;

        const allowedSet = new Set(allowed);
        const bounded: Record<string, LocaleByCountry[]> = {};

        for (const [country, locales] of Object.entries(allLocales.value)) {
            const kept = locales.filter((locale) => allowedSet.has(locale.locale));
            if (kept.length) bounded[country] = kept;
        }

        return bounded;
    });

    const addVariable = (variable: string) => {
        props.inputData.model.value = `${props.inputData.model.value} %${variable}%`;
    };

    const emit = defineEmits<{
        (e: 'update:locale', value: string): void;
        (e: 'label-clicked'): void;
    }>();
</script>

<template>
    <div :class="inputData.elementClassName.value">
        <div :class="[inputData.containerClassName.value, inputData.inlineLabel ? 'flex !flex-row-reverse items-center space-x-2 space-x-reverse' : '']">
            <div
                v-if="!inputData.withoutLabel && inputData.label.value"
                :class="inputData.labelContainerClassName.value"
                class="flex items-center justify-between w-full"
            >
                <label
                    :class="inputData.labelClassName?.value || (inputData.inlineLabel ? $theme('form.label_inline') : $theme('form.label') + ' self-end')"
                    :for="inputData.inputId.value"
                    @click="emit('label-clicked')"
                >
                    {{ inputData.label.value }}
                    <span
                        v-if="inputData.required"
                        class="text-red-500"
                        >*</span
                    >
                    <span
                        v-if="inputData.tooltip"
                        :data-tooltip="inputData.tooltip"
                    >
                        <i class="text-xs fa-regular fa-circle-info" />
                    </span>
                    <Submenu
                        v-if="inputData.translated"
                        relative-div-class="inline-block mb-2 ml-2"
                    >
                        <template #default="{ toggle }">
                            <span
                                class="px-2 py-1 text-neutral-600 bg-white transition-all border border-gray-200 rounded text-[11px] hover:bg-neutral-100 cursor-pointer"
                                @click.prevent="toggle"
                            >
                                <i class="mr-1 text-xs fa-regular fa-flag" />
                                <span class="inline-block uppercase">
                                    {{ inputData.locale.value }}
                                </span>
                            </span>
                        </template>
                        <template #submenu="{ hide }">
                            <div class="w-full min-w-[260px]">
                                <div
                                    v-for="(locales, country) in availableLocales"
                                    :key="`language-switcher-country-${country}`"
                                    :class="$theme('submenu.items')"
                                >
                                    <a
                                        v-for="availableLocale in locales"
                                        :key="availableLocale.locale"
                                        href="#"
                                        :class="$theme('submenu.item_without_icon', 'flex items-center justify-between')"
                                        @click.prevent="
                                            inputData.locale.value = availableLocale.locale;
                                            emit('update:locale', availableLocale.locale);
                                            hide();
                                        "
                                    >
                                        <div class="flex items-center space-x-2">
                                            <img
                                                class="h-[16px] bg-neutral-100 min-w-[18px]"
                                                :src="`https://trustup-io-shared-assets.ams3.cdn.digitaloceanspaces.com/images/translations/flags/${country}.svg`"
                                                alt=""
                                            />
                                            <span>
                                                {{ $t(`locales.native.${availableLocale.language}`) }}
                                            </span>
                                        </div>
                                        <span :data-tooltip="!inputData.hasTranslation(availableLocale.locale) ? $t('misc.missing_translation') : null">
                                            <i
                                                v-if="!inputData.hasTranslation(availableLocale.locale)"
                                                class="text-sm fa-regular fa-empty-set"
                                                :class="$theme('submenu.item_icon')"
                                            />
                                            <i
                                                v-if="availableLocale.locale === inputData.locale.value && inputData.hasTranslation(availableLocale.locale)"
                                                class="text-sm fa-regular fa-circle-check"
                                                :class="$theme('submenu.item_icon')"
                                            />
                                        </span>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </Submenu>
                    <FormPartialsVariablesSubmenu
                        v-if="inputData.withVariables"
                        relative-div-class="inline-block mb-2 ml-2"
                        submenu-position="left-0"
                        @add-variable="addVariable"
                    >
                        <template #default="{ toggle }">
                            <span
                                class="px-2 py-1 text-neutral-600 bg-white transition-all border border-gray-200 rounded text-[11px] hover:bg-neutral-100 cursor-pointer"
                                @click.prevent="toggle"
                            >
                                <i class="text-xs fa-regular fa-percentage" />
                            </span>
                        </template>
                    </FormPartialsVariablesSubmenu>
                </label>
                <div :class="['flex-none', labelRightClass]"><slot name="label-right" /></div>
            </div>
            <slot></slot>
            <span
                v-if="inputData.form?.errors?.has(inputData.inputName)"
                class="block text-xs text-red-600 mt-[4px]"
            >
                {{ inputData.form.errors.get(inputData.inputName) }}
            </span>
        </div>
    </div>
</template>
